import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { track } from "../lib/checkout";

const FAQS = [
  {
    q: "Why is this only ₹199?",
    a: "₹199 exists to remove price as an excuse. The workshop is intentionally kept accessible so serious experts can experience the frameworks before committing to anything higher-tier. What you'll walk away with in 90 minutes is worth significantly more — that's the whole point of the trust-first pricing.",
  },
  {
    q: "Do I need a large following for this to work?",
    a: "No. Coaches, consultants and founders with smaller audiences often move faster because they can implement without unlearning years of bad content habits. What matters is a clear offer, a defined audience and the system to connect the two — not follower count.",
  },
  {
    q: "I'm a complete beginner. Will I understand any of it?",
    a: "The workshop assumes you have an offer or expertise you want to monetise — not that you're an AI power user or seasoned creator. If you can join a Zoom call and take notes, you'll leave with a clear, actionable plan. No technical background required.",
  },
  {
    q: "What if I can't attend live?",
    a: "The live session is where the real work happens — live builds, walkthroughs and Q&A. Register anyway; a limited replay window is shared with registrants after the session. That said, attending live remains the single strongest predictor of actual implementation.",
  },
  {
    q: "I'm not a creator. I run a business / agency / coaching company. Is this for me?",
    a: "Yes — this was built for you. The system uses content and AI as a client-acquisition engine, not as a path to becoming an influencer. Coaches, consultants, agency owners and service founders tend to get the strongest applications from these frameworks.",
  },
  {
    q: "I don't have time to be a full-time creator. Does this still work?",
    a: "Yes, and that's precisely the point. The workshop teaches AI-first workflows that compress content, marketing and lead nurturing into a fraction of the manual effort. If you can invest 30–45 focused minutes a day, you can run this system.",
  },
  {
    q: "Is this just another AI gimmick?",
    a: "No. AI is one layer in the system — not the whole story. What most workshops skip is the connective tissue between positioning, content, capture, nurture and conversion. That's exactly what the 90 minutes is designed to install.",
  },
  {
    q: "What if I don't want to be on camera?",
    a: "Faceless and voice-cloned formats are covered in depth. Several of the strongest expert brands built recently never showed their face. You'll see the frameworks for building attention without needing to film yourself every day.",
  },
  {
    q: "I've bought courses before and never got results.",
    a: "Fair. The difference here is that this is an implementation workshop, not an information dump. You leave with a plan you can execute this week — plus the exact frameworks Nawaz uses inside his own business. Results still require you to actually implement.",
  },
  {
    q: "Why should I join now instead of waiting?",
    a: "Attention is a compounding asset. Every month you delay building the system, others are building it further ahead. There's also a practical reason: once the countdown hits zero, registration switches to the paid ₹1,999 tier.",
  },
  {
    q: "What makes the ViralX system different from other AI tools?",
    a: "ViralX isn't a single-purpose tool. It's an operating layer that combines content research, ideation, format analysis, distribution and monetisation into one connected workflow — replacing what usually takes a stack of disconnected tools. You'll see it running live inside the workshop.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(null);

  const toggle = (i) => {
    setOpen((prev) => {
      const next = prev === i ? null : i;
      if (next !== null) track("FAQ_Interaction", { index: i, question: FAQS[i].q });
      return next;
    });
  };

  return (
    <section className="relative z-10 px-4 py-10 md:py-14">
      <div className="max-w-4xl mx-auto">
        <p className="text-center text-[#C4FF3D] uppercase tracking-[0.28em] text-[11px] md:text-xs font-bold mb-3">
          FAQ
        </p>
        <h2 className="display-font text-center text-3xl md:text-5xl font-extrabold leading-tight mb-3">
          <span className="text-white">Frequently Asked</span>{" "}
          <span className="lime-box">Questions</span>
        </h2>
        <p className="text-center text-white/60 max-w-2xl mx-auto mb-10 text-sm md:text-base">
          Everything you need to know before reserving your seat.
        </p>

        <div className="space-y-3 md:space-y-4">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className={`faq-item ${isOpen ? "open" : ""} overflow-hidden`}>
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center justify-between gap-4 px-5 md:px-6 py-4 md:py-5 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="text-white font-semibold text-sm md:text-lg pr-2">{f.q}</span>
                  <span className="shrink-0 w-8 h-8 rounded-full bg-[#C4FF3D] flex items-center justify-center">
                    {isOpen ? (
                      <Minus className="w-4 h-4 text-[#0a0f0a]" strokeWidth={3} />
                    ) : (
                      <Plus className="w-4 h-4 text-[#0a0f0a]" strokeWidth={3} />
                    )}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-5 md:px-6 pb-5 md:pb-6 text-white/75 leading-relaxed border-t border-dashed border-[rgba(196,255,61,0.2)] pt-4 text-sm md:text-base">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
